import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { Button } from 'rizzui';
import DateField from '../../components/forms/DateField';
import { useFiltersPR } from '../../components/providers/FiltersProvider';
import { PrivilegeContext } from '../../components/priviledge/PriviledgeProvider';
import CustomSelect from '../../components/forms/CustomSelect';
import useCompanies from '../../hooks/api-calls/useCompanies';

interface DashboardFiltersProps { }

type DateFilterType = 'today' | 'week' | 'month' | 'year';

interface QuickFilter {
    type: DateFilterType;
    label: string;
}

const QUICK_FILTERS: QuickFilter[] = [
    { type: 'today', label: 'Hoy' },
    { type: 'week', label: 'Esta semana' },
    { type: 'month', label: 'Este mes' },
    { type: 'year', label: 'Este año' },
];

export const MODE_VIEW = [
    { value: 'daily', label: 'Diario' },
    { value: 'weekly', label: 'Semanal' },
    { value: 'monthly', label: 'Mensual' },
    { value: 'yearly', label: 'Anual' },
]

export const STATUS_TYPE = [
    { value: 'contract', label: 'Contrato' },
    { value: 'contract_product', label: 'Producto del contrato' },
    { value: 'contract_invoicing', label: 'Facturación del contrato' },
    { value: 'contract_incidence', label: 'Incidencia del contrato' },
    { value: 'contract_commission', label: 'Comisión del contrato' },
]

const DashboardFilters: React.FC<DashboardFiltersProps> = ({ }) => {

    // HOOKS 

    const { filters, updateFilters } = useFiltersPR();
    const { getCompaniesList } = useCompanies();
    const { userCan } = useContext(PrivilegeContext);

    const startDate = useMemo(
        () => (filters.filter_filters?.from ? moment(filters.filter_filters.from, 'YYYY-MM-DD').toDate() : null),
        [filters.filter_filters?.from]
    );
    const endDate = useMemo(
        () => (filters.filter_filters?.to ? moment(filters.filter_filters.to, 'YYYY-MM-DD').toDate() : null),
        [filters.filter_filters?.to]
    );

    // FUNCTIONS

    /**
     * Returns the date range based on the selected filter type.
     * @param type - The type of date filter.
     * @returns An object containing the start and end dates in 'YYYY-MM-DD' format.
     */
    const getDateRange = (type: DateFilterType): { from: string; to: string } => {
        const today = moment();

        switch (type) {
            case 'today':
                return {
                    from: today.format('YYYY-MM-DD'),
                    to: today.format('YYYY-MM-DD')
                };
            case 'week':
                return {
                    from: today.startOf('week').format('YYYY-MM-DD'),
                    to: today.endOf('week').format('YYYY-MM-DD')
                };
            case 'month':
                return {
                    from: today.startOf('month').format('YYYY-MM-DD'),
                    to: today.endOf('month').format('YYYY-MM-DD')
                };
            case 'year':
                return {
                    from: today.startOf('year').format('YYYY-MM-DD'),
                    to: today.endOf('year').format('YYYY-MM-DD')
                };
        }
    };

    /**
     * Handles quick date filter selection.
     * @param type - The type of date filter to apply.
     */
    const handleQuickDateFilter = (type: DateFilterType) => {
        const { from, to } = getDateRange(type);
        updateFilters({ from, to });
    };

    /**
     * Checks if a specific date filter is active.
     * @param type - The type of date filter to check.
     * @returns True if the filter is active, false otherwise.
     */
    const isFilterActive = (type: DateFilterType) => {
        const currentFrom = filters.filter_filters?.from;
        const currentTo = filters.filter_filters?.to;

        if (!currentFrom || !currentTo) return false;

        const { from: expectedFrom, to: expectedTo } = getDateRange(type);
        return currentFrom === expectedFrom && currentTo === expectedTo;
    };

    // RENDER

    return (
        <div className='flex items-center justify-end gap-4'>

            { userCan('list_company', 'companies') && (
                <CustomSelect
                    id="company"
                    isSearchable
                    placeholder="una empresa..."
                    options={getCompaniesList()}
                    value={filters.filter_filters?.company}
                    onChange={(selectedOptions: any) => {
                        const company = selectedOptions?.value ?? null;
                        if (filters.filter_filters?.company === company) return;
                        updateFilters({ company });
                    }}
                    noOption={() => 'No hay empresas disponibles'}
                />
            )}

            <div className='flex gap-2'>
                {QUICK_FILTERS.map(({ type, label }) => (
                    <Button
                        key={type}
                        size="sm"
                        variant={isFilterActive(type) ? 'solid' : 'outline'}
                        onClick={() => handleQuickDateFilter(type)}
                        className="h-9"
                    >
                        {label}
                    </Button>
                ))}
            </div>

            <DateField
                isClearable
                className="w-96"
                placeholderText="Selecciona las fechas"
                startDate={startDate}
                endDate={endDate}
                onChange={(date: any) => {
                    if (!date) {
                        if (filters.filter_filters?.from || filters.filter_filters?.to) {
                            updateFilters({ from: null, to: null });
                        }
                        return;
                    }

                    const from = moment(date[0]).format('YYYY-MM-DD');
                    const to = moment(date[1]).format('YYYY-MM-DD');

                    if (filters.filter_filters?.from === from && filters.filter_filters?.to === to) {
                        return;
                    }

                    updateFilters({ from, to });
                }}
                months={2}
                clea
            />
        </div>
    );
};

export default DashboardFilters;